import moment from 'moment';
import '../../util/momentLocales';

import callApi, { buildQuery } from '../../util/apiCaller';

import { sequence, pool } from '../../util/promises';
import { blobToFile, uploadFile, getFileContent } from '../../util/file';

import { transformCsvToJson, CONVERSATION_COVER_COLORS, CONVERSATION_PARTICIPANT_COLORS, BOOK_TITLE_MAX_LENGHT, BOOK_SUBTITLE_MAX_LENGHT } from './BookActions';
import { mergeTapbackReactions } from './tapbackReactions';
import { sortMessagesByDate, resolveConversationDateBounds } from './messagesSort';
import frData from '../../../Intl/localizationData/fr';
import enData from '../../../Intl/localizationData/en';
import deData from '../../../Intl/localizationData/de';
import esData from '../../../Intl/localizationData/es';
import itData from '../../../Intl/localizationData/it';
import nlData from '../../../Intl/localizationData/nl';
import ptData from '../../../Intl/localizationData/pt';
import jaData from '../../../Intl/localizationData/ja';
import koData from '../../../Intl/localizationData/ko';
import { getUserId } from '../User/UserActions';
import { setIsFetching, FETCH_LIMIT } from '../App/AppActions';
import { addError } from '../Error/ErrorActions';
import { enabledLanguages, defaultLanguage } from '../../../Intl/setup';
import { notifyError } from '../../components/Error/ErrorBoundary';

import * as instagramProvider from './providers/ConversationProviderInstagram';
import * as kakaotalkProvider from './providers/ConversationProviderKakaotalk';
import * as lineProvider from './providers/ConversationProviderLine';
import * as linkedinProvider from './providers/ConversationProviderLinkedin';
import * as messengerProvider from './providers/ConversationProviderMessenger';
import * as telegramProvider from './providers/ConversationProviderTelegram';
import * as snapchatProvider from './providers/ConversationProviderSnapchat';
import * as whatsappProvider from './providers/ConversationProviderWhatsapp';
import { setOrders } from '../Order/OrderActions';
import { getRandomString, slugify } from '../../util/text';
import { COVER_INCIPIT_VERSION, COVER_STYLE_INCIPIT, DEFAULT_COVER_STYLE, getCoverStyle } from './coverIncipit';

// Export Constants
export const SET_CONVERSATIONS = 'SET_CONVERSATIONS';
export const REMOVE_CONVERSATION = 'REMOVE_CONVERSATION';
export const SET_CONVERSATION_MESSAGES = 'SET_CONVERSATION_MESSAGES';
export const UPDATE_CONVERSATION_MESSAGE = 'UPDATE_CONVERSATION_MESSAGE';
export const UPDATE_CONVERSATION_MESSAGES_VISIBILITY = 'UPDATE_CONVERSATION_MESSAGES_VISIBILITY';
export const SET_BOOK_GENERATION = 'SET_BOOK_GENERATION';
export const SET_TUTORIAL = 'SET_TUTORIAL';

export const CONVERSATION_STATUS_PENDING = 'pending'; // on creation or on pause
export const CONVERSATION_STATUS_CONFIGURED = 'configured'; // creation is over and converstion is ready to be printed
export const CONVERSATION_STATUS_ONVALIDATION = 'onvalidation'; // Waiting for customer validation
export const CONVERSATION_STATUS_ONGENERATION = 'ongeneration'; // generating pdf files: covers + e-books
export const CONVERSATION_STATUS_ONPRODUCTION = 'onproduction'; // conversation is actually on generation
export const CONVERSATION_STATUS_DONE = 'done'; // conversation is generated and ready to be shipped (in case of real book)

export const MESSAGES_BLOCK_COUNT = 10000;
export const DATE_FORMAT_OUTPUT = 'YYYY-MM-DD HH:mm:ssZ';
export const MESSAGE_SYSTEM_SENDER_NAME = '___SYSTEM___';
export const MESSAGE_ATTACHMENTS_SEPARATOR = '|||'; // separtor for multiple attachements on csv
export const MESSAGE_ATTACHMENT_VIEWER_DATA_SEPARATOR = '---';
// Uniquement les fichiers chiffrés .enc. Le point doit être échappé :
// sinon « frenchwarren » ou « maxence » (…enc…) sont exclus de l'import.
export const CONVERSATION_ARCHIVE_EXCLUDED_FILES_REGEX = [/\.enc$/i];

const BUNDLED_INTL_CATALOGS = {
    fr: frData,
    en: enData,
    de: deData,
    es: esData,
    it: itData,
    nl: nlData,
    pt: ptData,
    ja: jaData,
    ko: koData,
};

export function getBundledMessage(id, defaultMessage = '') {
    const lang = (
        (typeof document !== 'undefined' && document.documentElement && document.documentElement.lang)
        || (typeof navigator !== 'undefined' && navigator.language)
        || 'fr'
    ).split('-')[0];
    const catalog = BUNDLED_INTL_CATALOGS[lang] || BUNDLED_INTL_CATALOGS.fr;
    return (catalog && catalog.messages && catalog.messages[id])
        || (BUNDLED_INTL_CATALOGS.en && BUNDLED_INTL_CATALOGS.en.messages && BUNDLED_INTL_CATALOGS.en.messages[id])
        || defaultMessage;
}

/** Pages de messages montrées dans l'aperçu, avant la carte « la suite vous attend ». */
export const CONVERSATION_PREVIEW_MESSAGE_PAGES = 16;

/** Couverture + préface + pages de messages + carte de fin. */
export const CONVERSATION_PREVIEW_PAGES_COUNT = CONVERSATION_PREVIEW_MESSAGE_PAGES + 3;

/** Au-delà de ce seuil, le redépôt d'archive est mis en avant (les deux options restent dispo). */
export const MISSING_MEDIAS_ARCHIVE_RECOMMENDED_THRESHOLD = 25;

/** Providers pour lesquels un redépôt zip/export peut combler les photos manquantes. */
export const MISSING_MEDIAS_ARCHIVE_PROVIDERS = ['whatsapp', 'messenger', 'instagram', 'telegram'];

export const CONVERSATIONS_EXTRACTROR_TYPES = {
	// Android & Ios are replaced by SMS
    // 'android': {
    //     steps: ['provider'],
    // },
    // 'ios': {
    //     steps: ['provider'],
    // },
    'instagram': {
        steps: ['provider', 'selection'],
        collectors: [
            {
                name: 'archive',
                helper: instagramProvider,
                fileImportAccept: 'application/zip,application/x-zip,.zip,.json',
                // messagesFileRegex: /^(messages\.json)/i,
                messagesFileRegex: [/message[s]?_[0-9]*.json/i], // New archive from Instagram/Messenger fusion in 2021
                isDefault: true,
                sort: 1,
            },
        ],
    },
    'kakaotalk': {
        steps: ['provider', 'options'],
        collectors: [
            {
                name: 'archive',
                helper: kakaotalkProvider,
                fileImportAccept: 'text/plain,.txt',
                messagesFileRegex: [/.*(\.txt)/i],
                uploadMessagesSourceFile: true,
                isDefault: true,
                sort: 1,
            },
        ],
    },
    'line': {
        steps: ['provider', 'options'],
        collectors: [
            {
                name: 'archive',
                helper: lineProvider,
                fileImportAccept: 'text/plain,.txt',
                messagesFileRegex: [/.*(\.txt)/i],
                uploadMessagesSourceFile: true,
                isDefault: true,
                sort: 1,
            },
        ],
    },
    'linkedin': {
        steps: ['provider', 'selection'],
        collectors: [
            {
                name: 'archive',
                helper: linkedinProvider,
                fileImportAccept: 'application/zip,application/x-zip,.zip,text/csv,.csv',
                // Profile.csv sert à identifier le titulaire de l'export (colonne FROM).
                messagesFileRegex: [/(^|\/)messages\.csv$/i, /(^|\/)profile\.csv$/i],
                isDefault: true,
                sort: 1,
            },
        ],
    },
    'messenger': {
        steps: ['provider', 'selection'],
        collectors: [
            {
                name: 'archive',
                helper: messengerProvider,
                fileImportAccept: 'application/zip,application/x-zip,.zip,.json',
                messagesFileRegex: [/(?!((archived_threads\/)|(message_requests\/)))message[s]?(_[0-9]*)?.json/i, /[a-z\s]+_(\d)+.json/i],
                isDefault: true,
                sort: 1,
            },
        ],
    },
    'snapchat': {
        steps: ['provider', 'selection'],
        collectors: [
            {
                name: 'archive',
                helper: snapchatProvider,
                fileImportAccept: 'application/zip,application/x-zip,.zip,.html,.json,application/json',
                messagesFileRegex: [
                    /(^|\/)chat_history\.json$/i,
                    /(^|\/)snap_history\.json$/i,
                    /(^|\/)chat_history\/subpage_[^/]+\.html$/i,
                    /(^|\/)subpage_[^/]+\.html$/i,
                    /(^|\/)[^/]+\.html$/i,
                ],
                // forceWithImages: true,
                isDefault: true,
                sort: 1,
            },
        ],
    },
    'telegram': {
        steps: ['provider', 'selection'],
        collectors: [
            {
                name: 'archive',
                helper: telegramProvider,
                fileImportAccept: 'application/zip,application/x-zip,.zip,.html,.json,application/json',
                messagesFileRegex: [/.*(\.html)/i, /(^|\/)[^/]+\.json$/i],
                // forceWithImages: true,
                isDefault: true,
                sort: 1,
            },
        ],
    },
    'whatsapp': {
        steps: ['provider', 'options'],
        collectors: [
            {
                name: 'archive',
                helper: whatsappProvider,
                fileImportAccept: 'application/zip,application/x-zip,.zip,.txt',
                messagesFileRegex: [/.*(\.txt)/i],
                // forceWithImages: true,
				uploadMessagesSourceFile: true,
                isDefault: true,
                sort: 1,
            },
        ],
    },
    'sms': {
        steps: ['provider'],
    },
};

export const CONVERSATION_EDIT_STEPS = ['cover', 'participants', 'dates', 'messages', 'preview', 'success'];

export const CONVERSATION_FLAGS = {
    missingMedias: 'MISSING_MEDIAS',
    cmykConversionFailed: 'CMYK_CONVERSION_FAILED',
};

/** True si le client doit réparer les médias avant reprise (flag sans VERIFIED). */
export function hasBlockingMissingMedias(conversation) {
	const flags = conversation?.flags || [];
	return flags.includes(CONVERSATION_FLAGS.missingMedias) && !flags.includes('VERIFIED');
}

/** Deep-link preview qui ouvre la modal recovery. */
export function getMissingMediasRecoveryPath(conversationId) {
	return `/conversation/${conversationId}/preview?missingMedias=1`;
}

export const TUTORIAL_VERSION = 1;

export function getConversationsPageRequest(page = 1, pageSize = FETCH_LIMIT, filters = {}, sorter = {}) {
    return dispatch => {
        return callApi(`conversations?all=1&start=${(page - 1) * pageSize}&limit=${pageSize}&${buildQuery(filters, 'filters')}&${buildQuery(sorter, 'sorter')}`).then(res => {
            res.conversations && dispatch(setConversations(res.conversations || []));
            return {
                ...res,
                isEnd: (res.conversations || []).length < pageSize,
            };
        }).catch(err => {
            return null;
        });
    };
}

export function getConversationsRequest(search = '', filters = null, sorter = { column: null, type: 'ASC' }, pager = { current: 0, size: FETCH_LIMIT }) {
    const recursiveFetch = (limit, start = 0, items = []) => {
        return new Promise((resolve, reject) => {
            callApi(`conversations?${search ? `search=${search}&` : ''}${filters ? `${Object.keys(filters).map(filterKey => `filters[${filterKey}]=${filters[filterKey]}`).join('&')}&` : ''}${sorter && sorter.column ? `sortBy=${sorter.column}&sortType=${sorter.type || 'ASC'}` : ''}&start=${start}&limit=${limit}`).then(res => {
                items = items.concat(res.conversations);
                if(!pager && res.conversations && res.conversations.length >= FETCH_LIMIT) {
                    return resolve(recursiveFetch(limit, start + limit, items));
                }
                resolve(items);
            }).catch(err => {
                reject(err);
            });
        });
    };

    return dispatch => {
        dispatch(setIsFetching('conversations'));
        return recursiveFetch((pager && pager.size) || FETCH_LIMIT, pager ? pager.current * pager.size : 0).then(conversations => {
            dispatch(setConversations(conversations || []));
            dispatch(setIsFetching('conversations', false));
            return conversations;
        }).catch(err => {
            dispatch(addError(err));
            return null;
        });
    };
}

export function getConversationsByUserRequest(userId) {
    return dispatch => {
        dispatch(setIsFetching('conversations'));
        return callApi(`conversations/${userId}`).then(result => {
            dispatch(setConversations(result.conversations, true));
            dispatch(setIsFetching('conversations', false));
            return result.conversations;
        }).catch(err => {
            return null;
        });
    };
}

export function getAllConversationsRequest() {
    return dispatch => {
        return dispatch(getConversationsRequest());
    };
}

export function getConversationRequest(conversationId) {
    return dispatch => {
        return callApi(`conversation/${conversationId}`).then(result => {
            result.conversation && dispatch(setConversations([result.conversation]));
            return result.conversation;
        }).catch(err => {
            return null;
        });
    };
}

/*
 * Applique les messages édités (overlay Mongo) sur la liste plate des messages CSV.
 * Aligné sur la logique serveur (conversation.helper.js) : une seule passe via une
 * Map par référence. Les éditions trouvées sont fusionnées, les messages ajoutés
 * par l'utilisateur (x-N, absents du CSV) sont poussés une seule fois — ce qui évite
 * les doublons d'affichage sur les conversations multi-blocs.
 */
export function applyEditedMessagesToCsvMessages(messages, editedMessages = []) {
	if(!editedMessages?.length) {
		return messages;
	}
	const refIndex = new Map();
	for(let i = 0; i < messages.length; i += 1) {
		refIndex.set(messages[i].reference, i);
	}
	editedMessages.forEach(editedMessage => {
		const messageIndex = refIndex.get(editedMessage.reference);
		if(messageIndex !== undefined) {
			messages[messageIndex] = mergeCsvMessageWithEdited(messages[messageIndex], editedMessage);
		} else {
			messages.push(editedMessage);
		}
	});
	return messages;
}

/*
 * Déduplication défensive par référence : filet de sécurité contre d'éventuelles
 * données historiques dupliquées. Conserve la première occurrence.
 */
function dedupeMessagesByReference(messages) {
	const seen = new Set();
	return (messages || []).filter(message => {
		if(!message?.reference) {
			return true;
		}
		if(seen.has(message.reference)) {
			return false;
		}
		seen.add(message.reference);
		return true;
	});
}

export function getConversationMessagesRequest(conversation, filename = null, editedMessages = null, { forceRefresh = false } = {}) {
	return (dispatch, getState) => {
		// Court-circuit : si les messages sont déjà en Redux (tiroir ouvert depuis l'aperçu,
		// changement de dates, régénération...), on les réutilise au lieu de re-télécharger
		// tous les blocs CSV S3. Le filtrage dates et l'exclusion des messages masqués sont
		// faits en aval (generateBooks), donc le cache reste valide. forceRefresh permet de
		// forcer le rechargement (ex. nouveau fichier importé).
		if(!forceRefresh && !filename && getState) {
			const existing = getConversationMessages(getState(), conversation._id);
			if(existing?.length) {
				return Promise.resolve(existing);
			}
		}
		return getConversationMessagesFilesRequest(conversation, filename, editedMessages).then(messages => {
			messages = sortMessagesByDate(dedupeMessagesByReference(messages));
			// Rattache les réactions tapback iOS à leur message d'origine (ordre chronologique requis,
			// garanti par le sort ci-dessus). Les sources restent dans le tableau (visibles en édition) ;
			// elles sont exclues du livre en aval via isTapbackSource.
			messages = mergeTapbackReactions(messages);
			dispatch(setConversationMessages(conversation._id, messages));
			return messages;
		});
    };
}

export function getConversationMessagesFilesRequest(conversation, filename = null, editedMessages = null) {
	if(filename) {
		// Téléchargement d'un bloc CSV brut. La fusion avec les éditions Mongo est faite
		// une seule fois en aval (applyEditedMessagesToCsvMessages), jamais par bloc.
		return getFileContent(`conversations/${conversation._id}/${filename}`).then(response => {
			if(response) {
				return response.text().then(text => {
					if(text) {
						return transformCsvToJson(text, filename.replace(/\D/g, ''), true);
					}
					return null;
				});
			}
			return null;
		}).catch(err => {
			console.error(err);
			return null;
		});
	}
	return (editedMessages ? Promise.resolve({ messages: editedMessages }) : callApi(`conversation/${conversation._id}/messages`)).then(result => {
		// If no filename defined => fetch all messages of every blocks
		const conversationDates = getConversationDates(conversation);
		const selectedMessagesBlocks = conversation.messagesBlocks.filter(messagesBlock => !conversation.dates || !conversation.dates.start || conversation.dates.end || isMessageBlockIncluded(messagesBlock, conversation.dates.start, conversation.dates.end));
		conversation && conversationDates && console.log(`Conversation time: ${conversationDates.start} -> ${conversationDates.end}`);
		// Téléchargement des blocs CSV bruts en parallèle (Promise.all conserve l'ordre).
		return Promise.all(
			selectedMessagesBlocks.map(messagesBlock => (
				getConversationMessagesFilesRequest(conversation, messagesBlock.filename)
			)),
		).then(results => {
			let messages = [];
			results.forEach(messagesList => {
				messages = messages.concat(messagesList || []);
			});
			// Fusion unique des éditions Mongo sur l'ensemble des blocs concaténés.
			return applyEditedMessagesToCsvMessages(messages, result?.messages || []);
		});
	}).catch(err => {
		return null;
	});
}

export function getConversationHighlightsRequest(conversationId) {
	return callApi(`conversation/${conversationId}/highlights`);
}

export function recordConversationHighlightEvent(conversationId, event, card = '') {
	return callApi(`conversation/${conversationId}/highlights/event`, 'post', { event, card }).catch(() => null);
}

export function computeConversationHighlightsRequest(conversation) {
	return dispatch => {
		const hasHighlightsData = !!(conversation.highlights?.startDate || conversation.highlights?.messagesCount);
		const computePromise = hasHighlightsData
			? Promise.resolve(conversation.highlights)
			: dispatch(getConversationMessagesRequest(conversation)).then(messages => getHighlights(conversation, messages));

		return computePromise.then(highlights => {
			const privateHighlights = { ...(highlights || conversation.highlights || {}) };
			delete privateHighlights.isPublic;
			return dispatch(editConversationRequest({
				_id: conversation._id,
				highlights: privateHighlights,
			})).then(() => privateHighlights);
		});
	};
}

// Aperçu WYSIWYG : récupère le HTML d'un sheet rendu via le même template + book.css que le PDF final.
export function getBookPreviewSheetRequest(conversationId, pages, format = 'a5', editionType = null, bookIndex = 0, bookDirection = null) {
	return callApi(`conversation/${conversationId}/preview-sheet`, 'post', { pages, format, editionType, bookIndex, bookDirection }).then(result => result && result.html);
}

/**
 * Vérifie si une conversation identique existe déjà sur le compte (hors SMS).
 * Match strict : provider + participants + messages + photos + médias.
 */
export function checkConversationDuplicateRequest({
	provider,
	participants,
	messagesCount,
	imagesCount = 0,
	mediasCount = 0,
	excludeId = null,
	userId = null,
} = {}) {
	return () => {
		if(!provider || provider === 'sms') {
			return Promise.resolve(null);
		}
		return callApi('conversation/check-duplicate', 'post', {
			provider,
			participants,
			messagesCount,
			imagesCount,
			mediasCount,
			excludeId,
			userId,
		}).then(result => result?.conversation || null).catch(() => null);
	};
}

export function editConversationRequest(conversation, blockAlertUser = true) {
    return dispatch => {
        return callApi('conversation/edit', 'post', { conversation, blockAlertUser }).then(result => {
            // if(fetchAll) {
            //     return dispatch(getConversationsRequest()).then(conversations => {
            //         return conversations.find(conversation => conversation._id === result.conversation._id);
            //     });
            // }
            // dispatch(setConversations([conversation]));
            if(result && result.conversation) {
                return dispatch(getConversationRequest(result.conversation._id));
            }
            return result && result.conversation;
        }).catch(err => {
            return null;
        });
    };
}

// options.skipKeys : Set des clés S3 déjà transférées (reprise) — ignorées sans re-PUT.
// options.onKeyDone : callback(clé) appelé après chaque fichier confirmé, pour le checkpoint.
export function uploadConversationMessagesFilesRequest(conversation, messagesBlocks, onProgress = percentile => {}, options = {}) {
    if(!conversation) {
        return Promise.reject(new Error('NoConversation'));
    }
    const { skipKeys = null, onKeyDone = null } = options;
    const rootPath = `conversations/${conversation._id}/`;
    const percentile = 100 / (messagesBlocks.length);
    return sequence([...messagesBlocks], messagesBlock => {
        return new Promise((resolveBlock, rejectBlock) => {
            const key = `${rootPath}${messagesBlock.filename}`;
            if(skipKeys && skipKeys.has(key)) {
                onProgress(percentile);
                resolveBlock(true);
                return;
            }
            const messageFile = blobToFile(new Blob([messagesBlock.content], { type: 'text/csv' }), messagesBlock.filename);
            uploadFile(key, messageFile, 'text/csv').then(result => {
                onKeyDone && onKeyDone(key);
                onProgress(percentile);
                resolveBlock(result);
            }).catch(rejectBlock);
        });
    });
}

// options.concurrency : nombre d'uploads en parallèle (photos = 4, médias/vidéos = 1).
export function uploadConversationMediasFilesRequest(conversation, mediasEntries, onProgress = percentile => {}, options = {}) {
    if(!conversation) {
        return Promise.reject(new Error('NoConversation'));
    }
    const { skipKeys = null, onKeyDone = null, concurrency = 4 } = options;
    const rootPath = `conversations/${conversation._id}/images/`;
    const percentile = 100 / (mediasEntries.length);
    return pool([...mediasEntries], mediaEntry => {
        return new Promise(resolveImage => {
            const extension = mediaEntry.filename.split('.').pop().toLowerCase();
            const key = `${rootPath}${mediaEntry.filename}`.toLowerCase();
            if(skipKeys && skipKeys.has(key)) {
                onProgress(percentile);
                resolveImage(mediaEntry.filename.toLowerCase());
                return;
            }
            const imageFile = blobToFile(
				mediaEntry.content != null ? mediaEntry.content : mediaEntry,
				mediaEntry.filename,
			);
            uploadFile(key, imageFile, `${mediaEntry.type}/${extension}`).then(result => {
                onKeyDone && onKeyDone(key);
                onProgress(percentile);
                resolveImage(mediaEntry.filename.toLowerCase());
            }).catch(err => {
                notifyError(err, { conversationId: conversation._id, filename: mediaEntry.filename, type: mediaEntry.type });
                resolveImage(null);
            });
        });
    }, concurrency);
}
export function uploadConversationSourceFileRequest(conversation, sourceEntries, onProgress = percentile => {}, options = {}) {
    if(!conversation) {
        return Promise.reject(new Error('NoConversation'));
    }
    const { skipKeys = null, onKeyDone = null } = options;
    const rootPath = `conversations/${conversation._id}/sources/`;
    const percentile = 100 / (sourceEntries.length);
    let count = 0;
    return sequence([...sourceEntries], sourceEntry => {
        return new Promise(resolveSource => {
            const extension = sourceEntry.filename.split('.').pop().toLowerCase();
            const sourceFile = sourceEntry?.lastModifiedDate ? sourceEntry : blobToFile(sourceEntry.content, sourceEntry.filename);
			const path = `${rootPath}${slugify(sourceEntry.filename.replace(`.${extension}`, ''))}-${count + 1}.${extension}`.toLowerCase();
            if(skipKeys && skipKeys.has(path)) {
                onProgress(percentile);
                count += 1;
                resolveSource(path);
                return;
            }
            uploadFile(path, sourceFile, `${sourceEntry.type}/${extension}`).then(result => {
                onKeyDone && onKeyDone(path);
                onProgress(percentile);
                setTimeout(() => {
                    count += 1;
                    resolveSource(path);
                }, count % 100 === 0 ? 2000 : 50);
            }).catch(err => {
                notifyError(err, { conversationId: conversation._id, filename: sourceEntry.filename, type: sourceEntry.type });
                resolveSource(null);
            });
        });
    });
}

export function editMessageRequest(conversation, message, blockUpdateStore = false) {
	return dispatch => {
		return callApi(`conversation/${conversation._id}/message/edit`, 'post', { message }).then(result => {
			!blockUpdateStore && result.message && dispatch(updateConversationMessage(conversation._id, result.message));
			return result?.message;
		}).catch(err => {
			return null;
		});
	};
}

// Annule toutes les modifications utilisateur (masquage / édition / ajout) : le
// serveur supprime l'overlay Mongo, puis on recharge les messages depuis le CSV
// d'origine (forceRefresh pour ignorer le cache Redux).
export function resetConversationMessagesRequest(conversation) {
	return dispatch => {
		return callApi(`conversation/${conversation._id}/messages/reset`, 'post').then(result => {
			if(result && result.ok) {
				return dispatch(getConversationMessagesRequest(conversation, null, null, { forceRefresh: true }));
			}
			return null;
		}).catch(err => {
			console.error(err);
			return null;
		});
	};
}

// État de visibilité actuel des références visées, pour pouvoir revenir en arrière
// si l'API échoue après une mise à jour optimiste.
function getMessagesVisibilitySnapshot(store, conversationId, references) {
	const messages = store ? getConversationMessages(store, conversationId) : null;
	if(!messages || !messages.length) {
		return null;
	}
	const referenceSet = new Set(references);
	const snapshot = [];
	for(let index = 0; index < messages.length; index += 1) {
		const message = messages[index];
		if(referenceSet.has(message.reference)) {
			snapshot.push({ reference: message.reference, isHidden: !!message.isHidden, isEdited: !!message.isEdited });
		}
	}
	return snapshot.length ? snapshot : null;
}

// Masque / réaffiche en masse des messages (outil « Masquer une période », sélection
// et bouton œil unitaire). N'envoie que les références (payload léger) ; le serveur
// n'écrit que isHidden via bulkWrite natif.
// Mise à jour optimiste : le store est modifié avant l'aller-retour API pour que la
// liste réagisse immédiatement, puis restauré tel quel si la requête échoue.
export function setConversationMessagesVisibilityRequest(conversation, references, isHidden) {
	return (dispatch, getState) => {
		if(!references?.length) {
			return Promise.resolve({ ok: true, modifiedCount: 0 });
		}
		const snapshot = getState ? getMessagesVisibilitySnapshot(getState(), conversation._id, references) : null;
		dispatch(updateConversationMessagesVisibility(conversation._id, references, isHidden));
		const rollback = () => {
			snapshot && dispatch(restoreConversationMessagesVisibility(conversation._id, snapshot));
		};
		return callApi(`conversation/${conversation._id}/messages/visibility`, 'post', { references, isHidden }).then(result => {
			if(!result || !result.ok) {
				rollback();
				return null;
			}
			return result;
		}).catch(err => {
			console.error(err);
			rollback();
			return null;
		});
	};
}

export function mergeConversationsRequest(conversationIds) {
    return dispatch => {
        return callApi('conversation/merge', 'post', { conversations: conversationIds }).then(result => {
            return result.ok;
        }).catch(err => {
            return null;
        });
    };
}

export function pauseOrResumeConversationRequest(conversationId, orderId, isResume = false) {
	return dispatch => {
		return callApi(`conversation/${conversationId}/${isResume ? 'resume' : 'pause'}`, 'post', { orderId }).then(res => {
			res.order && dispatch(setOrders([res.order]));
			res.conversation && dispatch(setConversations([res.conversation]));
			return res;
		}).catch(err => {
			console.error('pauseOrResumeConversationRequest', err);
			return null;
		});
	};
}

export function getConversationMissingMediasRequest(conversationId, { rescan = false } = {}) {
	return dispatch => {
		const query = rescan ? '?rescan=1' : '';
		return callApi(`conversation/${conversationId}/missing-medias${query}`).then(res => {
			res?.conversation && dispatch(setConversations([res.conversation]));
			return res;
		}).catch(err => {
			console.error('getConversationMissingMediasRequest', err);
			return null;
		});
	};
}

export function rescanConversationMissingMediasRequest(conversationId, { resumeIfFixed = true } = {}) {
	return dispatch => {
		return callApi(`conversation/${conversationId}/missing-medias/rescan`, 'post', { resumeIfFixed }).then(res => {
			res?.conversation && dispatch(setConversations([res.conversation]));
			res?.order && dispatch(setOrders([res.order]));
			return res;
		}).catch(err => {
			console.error('rescanConversationMissingMediasRequest', err);
			return null;
		});
	};
}

export function ignoreConversationMissingMediaRequest(conversationId, { reference, filename }) {
	return dispatch => {
		return callApi(`conversation/${conversationId}/missing-medias/ignore`, 'post', { reference, filename }).then(res => {
			res?.conversation && dispatch(setConversations([res.conversation]));
			res?.order && dispatch(setOrders([res.order]));
			return res;
		}).catch(err => {
			console.error('ignoreConversationMissingMediaRequest', err);
			return null;
		});
	};
}

export function generateRequest(conversationId, orderId) {
	return dispatch => {
		return callApi(`conversation/${conversationId}/generate/${orderId}`).then(res => {
			return res?.ok;
		}).catch(err => {
			return null;
		});
	};
}

export function launchRequest(conversationId) {
	return dispatch => {
		return callApi(`conversation/${conversationId}/launch`).then(res => {
			return res?.ok;
		}).catch(err => {
			return null;
		});
	};
}

export function removeConversationRequest(conversationId) {
    return dispatch => {
        return callApi('conversation/remove', 'delete', { conversation: { _id: conversationId } }).then(result => {
            result && result.ok && dispatch(removeConversation(conversationId));
            return result.ok;
        }).catch(err => {
            return null;
        });
    };
}

// Getters
export function getConversations(store, user = null) {
    return store.conversations.data.filter(conversation => !user || getConversationUserId(conversation) === getUserId(user));
}

export function getConversation(store, conversationId) {
    return store.conversations.data.find(conversation => conversation._id === conversationId);
}

export function getConversationMessagesLoaded(store, conversationId) {
    if(store.conversations.messages && store.conversations.messages[conversationId]) {
        return Object.keys(store.conversations.messages[conversationId]).sort((a, b) => b - a);
    }
    return [];
}

export function getConversationMessages(store, conversationId) {
    if(store.conversations.messages && (store.conversations?.messages || [])[conversationId]) {
        // let messages = [];
        // getConversationMessagesLoaded(store, conversationId).forEach(index => {
        //     messages = messages.concat(store.conversations.messages[conversationId][index]);
        // });
        // return messages;
		return store.conversations.messages[conversationId];
    }
    return [];
}

export function getBookGeneration(store, conversationId) {
    return store.conversations.bookGeneration[conversationId] || null;
}

export function isTutorialPassed(store, key) {
    // if(process.env.NODE_ENV === 'development') {
    //     return false;
    // }
    return (store.conversations.tutorials || {})[key] && (store.conversations.tutorials || {})[key] >= TUTORIAL_VERSION;
}

// Helper
export function getConversationId(conversation) {
    return conversation && conversation._id ? conversation._id : conversation;
}

export function getConversationUserId(conversation) {
    return conversation && ((conversation.user && conversation.user._id) || conversation.user);
}

export function getConversationDates(conversation) {
	const dates = conversation?.dates || {};
	const bounds = resolveConversationDateBounds(dates, conversation?.messagesBlocks || []);
	return {
		...dates,
		...bounds,
	};
}

// Nouvelles bornes sans effacer les autres clés (generated, downloaded, simulated).
// L'écran Dates n'envoie sinon que start et end, et l'enregistrement remplace tout l'objet.
export function withConversationDateBounds(dates = {}, start, end) {
	return {
		...(dates || {}),
		start,
		end,
	};
}

// Bornes du livre étendues pour inclure un message ajouté / déplacé hors plage,
// ou null si la date est déjà comprise dans la plage (rien à sauvegarder).
//
// On part des bornes RÉSOLUES (getConversationDates) et non de conversation.dates :
// tant que l'utilisateur n'est pas passé par l'écran /dates, dates vaut {} alors que
// l'affichage et la génération retombent déjà sur les bornes des blocs d'import. Se
// fier à dates seul laissait donc le message en base mais invisible partout.
//
// La paire start/end est toujours réécrite en entier : resolveConversationDateBounds
// ignore une borne orpheline et repartirait des blocs, annulant l'extension.
export function extendConversationDatesForMessage(conversation, messageDate) {
	const messageTime = messageDate ? moment.utc(messageDate).valueOf() : NaN;
	if(!conversation || Number.isNaN(messageTime)) {
		return null;
	}
	// Mêmes conversions que les filtres d'affichage et de génération du livre
	// (message en UTC, borne avec son fuseau) : on étend exactement dans les cas
	// où le message aurait été exclu.
	const { start, end } = getConversationDates(conversation);
	const startTime = start ? moment(start).valueOf() : null;
	const endTime = end ? moment(end).valueOf() : null;
	const extendsStart = startTime !== null && !Number.isNaN(startTime) && messageTime < startTime;
	const extendsEnd = endTime !== null && !Number.isNaN(endTime) && messageTime > endTime;
	if(!extendsStart && !extendsEnd) {
		return null;
	}
	return {
		...(conversation.dates || {}),
		start: extendsStart ? moment.utc(messageDate).format(DATE_FORMAT_OUTPUT) : start,
		// Fin de la minute : le dernier message de la minute reste inclus, comme la
		// borne par défaut de l'écran /dates.
		end: extendsEnd ? moment.utc(messageDate).endOf('minute').format(DATE_FORMAT_OUTPUT) : end,
	};
}

// Résout la couverture d'un tome. Fusion champ par champ : les clés présentes sur
// books[i].cover priment (y compris imageFront vide = suppression volontaire), les
// clés absentes retombent sur conversation.cover. Évite qu'un cover de tome partiel
// / obsolète fasse ignorer photo et titres à jour du global (preview mono-tome vs PDF).
export function getConversationCover(conversation, bookIndex) {
    const globalCover = conversation.cover || {};
    if(bookIndex != null && bookIndex >= 0) {
        const bookCover = ((conversation.books || [])[bookIndex] || {}).cover;
        if(!bookCover) {
            return globalCover;
        }
        return { ...globalCover, ...bookCover };
    }
    return globalCover;
}
export function getConversationCoverData(conversation, name, bookIndex) {
    return getConversationCover(conversation, bookIndex)[name] || '';
}

// Plusieurs tomes : un seul style pour toute la conversation, celui du premier tome.
// Le choix Polaroid ou « 1er message » est recopié partout à l'enregistrement.
export function getEffectiveCoverStyle(conversation, bookIndex) {
    const books = (conversation || {}).books || [];
    const index = books.length > 1 ? 0 : bookIndex;
    return getCoverStyle(getConversationCover(conversation || {}, index)) || DEFAULT_COVER_STYLE;
}

export function applySharedCoverStyle(conversation, style) {
    const books = (conversation || {}).books || [];
    if(books.length <= 1) {
        return conversation;
    }
    const resolved = getCoverStyle({ style });
    const version = resolved === COVER_STYLE_INCIPIT ? COVER_INCIPIT_VERSION : 2;
    return {
        ...conversation,
        cover: {
            ...(conversation.cover || {}),
            style: resolved,
            version,
        },
        books: books.map(book => ({
            ...book,
            cover: {
                ...(book.cover || {}),
                style: resolved,
                version,
            },
        })),
    };
}

// Seuls le template V2 et la couverture actuelle savent s'ouvrir par la droite. Les
// couvertures historiques ont des fonds imprimés dessinés pour une ouverture à gauche :
// leur intérieur doit rester relié du même côté, bulles en arabe comprises.
export function canBookOpenFromRight(conversation, bookIndex) {
    const cover = getConversationCover(conversation || {}, bookIndex);
    const coverColor = CONVERSATION_COVER_COLORS[getEffectiveCoverColor(conversation, bookIndex)] || {};
    const isLegacyCover = (!cover.version || cover.version === 1) && !!coverColor.legacy;
    const version = cover.version || ((conversation || {}).cover || {}).version || 2;
    return !isLegacyCover && version >= 2;
}

export const DEFAULT_COVER_COLOR = 'sauge';

// Couleurs posées toutes seules par l'ancienne usine. Sans choix explicite,
// elles redeviennent le vert, puis la palette par tome.
const LEGACY_FACTORY_COVER_COLORS = ['blue-pastel', 'blush'];

function isAutomaticCoverColor(color) {
    return !color || color === DEFAULT_COVER_COLOR || LEGACY_FACTORY_COVER_COLORS.includes(color);
}

function migrateCoverColor(stored) {
    if(isAutomaticCoverColor(stored)) {
        return DEFAULT_COVER_COLOR;
    }
    return stored;
}

function getBookCoverRecord(conversation, bookIndex) {
    if(bookIndex == null || bookIndex < 0) {
        return null;
    }
    return ((conversation?.books || [])[bookIndex] || {}).cover || null;
}

// Couleur posée par l'utilisateur sur ce tome (édition directe ou « appliquer à tous »).
export function isCoverColorUserChosen(cover) {
    return !!(cover && (cover.customized || cover.colorCustomized));
}

// Teinte de couverture par index de tome, comme les bulles des participants.
export function getDefaultCoverColorForIndex(index = 0) {
    const colors = getAvailableCoverColors();
    if(!colors.length) {
        return DEFAULT_COVER_COLOR;
    }
    const safeIndex = Number.isInteger(index) && index >= 0 ? index : 0;
    return colors[safeIndex % colors.length];
}

export function getEffectiveCoverColor(conversation, bookIndex = null) {
    const globalColor = (conversation?.cover || {}).color;
    const bookCover = getBookCoverRecord(conversation, bookIndex);
    const ownColor = bookCover?.color;

    const chosenOnBook = isCoverColorUserChosen(bookCover);
    const chosenOnGlobal = isCoverColorUserChosen(conversation?.cover);

    if(bookIndex == null || bookIndex < 0) {
        if(chosenOnGlobal && globalColor) {
            return globalColor;
        }
        return migrateCoverColor(globalColor);
    }

    if(bookIndex === 0) {
        const stored = ownColor || globalColor;
        if((chosenOnBook || chosenOnGlobal) && stored) {
            return stored;
        }
        if(stored && !isAutomaticCoverColor(stored)) {
            return stored;
        }
        return DEFAULT_COVER_COLOR;
    }

    // Tomes suivants : une couleur cliquée reste. Sinon la palette continue,
    // une teinte différente par tome, en partant du vert.
    if(ownColor && chosenOnBook) {
        return ownColor;
    }

    return getDefaultCoverColorForIndex(bookIndex);
}

const LOCALE_MESSAGES = {
	fr: frData.messages,
	en: enData.messages,
	de: deData.messages,
	es: esData.messages,
	it: itData.messages,
	nl: nlData.messages,
	pt: ptData.messages,
	ja: jaData.messages,
	ko: koData.messages,
};

export function normalizeCoverLocale(lang) {
	const short = `${lang || ''}`.split(/[-_]/)[0].toLowerCase();
	return enabledLanguages.includes(short) ? short : defaultLanguage;
}

// Langue à persister sur le livre quand le client change la langue du site. Renvoie null
// quand il n'y a rien à faire : conversation déjà commandée (la langue est figée avec le
// reste du livre) ou déjà alignée. La valeur renvoyée est normalisée, sinon une locale
// comme en-US ne correspondrait jamais à ce qui est stocké et la mise à jour repartirait
// en boucle.
export function getBookLanguageToAlign(conversation, locale) {
	if(!conversation || conversation.status !== CONVERSATION_STATUS_PENDING) {
		return null;
	}
	const lang = normalizeCoverLocale(locale);
	return conversation.language === lang ? null : lang;
}

export function getLocalizedMessage(lang, id, values = {}) {
	const locale = normalizeCoverLocale(lang);
	const messages = LOCALE_MESSAGES[locale] || LOCALE_MESSAGES[defaultLanguage];
	let template = messages[id] || LOCALE_MESSAGES[defaultLanguage][id] || id;
	Object.keys(values).forEach(key => {
		template = template.replace(new RegExp(`\\{${key}\\}`, 'g'), values[key]);
	});
	return template;
}

function truncateCoverTitle(text, maxLength) {
	const trimmed = `${text || ''}`.trim();
	if(trimmed.length <= maxLength) {
		return trimmed;
	}
	return trimmed.slice(0, maxLength).trim();
}

function getCoverParticipantNames(conversation) {
	return getConversationParticipants(conversation)
		.filter(participant => (participant.name || '') !== MESSAGE_SYSTEM_SENDER_NAME)
		.map(participant => (participant.displayName || participant.name || '').trim())
		.filter(Boolean)
		.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

function resolveCoverFormatMessage(locale, formatMessage) {
	const lang = normalizeCoverLocale(locale);
	// Toujours les catalogues bundlés, indexés par la locale demandée.
	// Ne pas réutiliser intl.formatMessage : au montage /preview le store intl
	// part encore du français SSR, donc le sous-titre restait « Du … au … »
	// pour toutes les langues jusqu'à un rechargement.
	if(formatMessage) {
		return (id, values = {}) => {
			const bundled = getLocalizedMessage(lang, id, values);
			if(bundled && bundled !== id) {
				return bundled;
			}
			return formatMessage(id, values);
		};
	}
	return (id, values = {}) => getLocalizedMessage(lang, id, values);
}

// Bornes de dates d'un livre déduites directement de ses pages (1er / dernier message
// réellement inclus). Sert à l'aperçu : les tomes générés côté client portent leurs pages
// bien avant que leurs dates ne soient persistées en base.
export function getBookMessagesDateBounds(book) {
	const pages = (book || {}).pages || [];
	let start = '';
	let end = '';
	pages.forEach(page => {
		const messages = page.messages || [];
		if(messages.length) {
			if(!start) {
				start = messages[0].date;
			}
			end = messages[messages.length - 1].date;
		}
	});
	return { start, end };
}

function getCoverDateBounds(conversation, bookIndex, { previewBook = null, editionType = null } = {}) {
	// Best-Of et version numérique : un seul volume couvrant toute la conversation
	// → sous-titre sur les dates globales (1er / dernier message), comme avant.
	if(editionType === 'bestof' || editionType === 'pdf') {
		return getConversationDates(conversation);
	}
	// Aperçu collection complète : tant que les dates du tome ne sont pas persistées,
	// on les déduit du tome généré (1er / dernier message réellement inclus).
	if(previewBook) {
		const previewBounds = getBookMessagesDateBounds(previewBook);
		if(previewBounds.start && previewBounds.end) {
			return previewBounds;
		}
	}
	const books = conversation.books || [];
	if(bookIndex != null && books.length > 1 && books[bookIndex]) {
		return { start: books[bookIndex].start, end: books[bookIndex].end };
	}
	return getConversationDates(conversation);
}

function formatCoverDateRange(conversation, bookIndex, locale, formatMessage, options = {}) {
	const { start, end } = getCoverDateBounds(conversation, bookIndex, options);
	if(!start || !end) {
		return '';
	}

	const lang = normalizeCoverLocale(locale);
	const dateFormats = ['D MMMM YYYY', 'D MMM YYYY', 'YYYY'];
	for(const dateFormat of dateFormats) {
		const startLabel = moment.utc(start).locale(lang).format(dateFormat);
		const endLabel = moment.utc(end).locale(lang).format(dateFormat);
		const range = formatMessage('conversationDateRange', { start: startLabel, end: endLabel });
		if(range.length <= BOOK_SUBTITLE_MAX_LENGHT) {
			return range;
		}
	}

	const startLabel = moment.utc(start).locale(lang).format('YYYY');
	const endLabel = moment.utc(end).locale(lang).format('YYYY');
	return truncateCoverTitle(formatMessage('conversationDateRange', { start: startLabel, end: endLabel }), BOOK_SUBTITLE_MAX_LENGHT);
}

function getCoverFirstName(name) {
	const trimmed = `${name || ''}`.trim();
	if(!trimmed) {
		return '';
	}
	const withoutProvider = trimmed.split('@')[0].trim();
	return (withoutProvider.split(/\s+/)[0] || withoutProvider).trim();
}

function getDefaultCoverTitleFrom(conversation, locale, formatMessage) {
	const names = getCoverParticipantNames(conversation);

	if(names.length >= 3) {
		return truncateCoverTitle(formatMessage('conversationCoverDefaultTitleGroup'), BOOK_TITLE_MAX_LENGHT);
	}

	if(names.length === 2) {
		const fullTitle = formatMessage('conversationCoverDefaultTitleTwoNames', {
			name1: names[0],
			name2: names[1],
		});
		if(fullTitle.length <= BOOK_TITLE_MAX_LENGHT) {
			return fullTitle;
		}
		return truncateCoverTitle(formatMessage('conversationCoverDefaultTitleTwoNames', {
			name1: getCoverFirstName(names[0]),
			name2: getCoverFirstName(names[1]),
		}), BOOK_TITLE_MAX_LENGHT);
	}

	return truncateCoverTitle(formatMessage('conversationCoverDefaultTitleSingle'), BOOK_TITLE_MAX_LENGHT);
}

function getDefaultCoverTitleTo(conversation, bookIndex, locale, formatMessage, options = {}) {
	// Si le type d'édition est fourni (aperçu inline / couverture WYSIWYG), il prime :
	// bascule immédiate du préfixe « Best-Of · » sans attendre la persistance de options.type.
	// Sinon (PDF, cartes…), on retombe sur l'option persistée / le type des tomes.
	const isBestOf = options.editionType
		? options.editionType === 'bestof'
		: (getConversationOption(conversation, 'type') === 'bestof'
			|| (conversation.books || []).some(book => book.type === 'bestof'));
	const dateRange = formatCoverDateRange(conversation, bookIndex, locale, formatMessage, options);

	if(!dateRange) {
		return '';
	}

	if(isBestOf) {
		const bestOfLabel = formatMessage('conversationPreviewTypeBestofTitle');
		const withBestOf = `${bestOfLabel} · ${dateRange}`;
		if(withBestOf.length <= BOOK_SUBTITLE_MAX_LENGHT) {
			return withBestOf;
		}
	}

	return truncateCoverTitle(dateRange, BOOK_SUBTITLE_MAX_LENGHT);
}

export function getDefaultCoverTitles(conversation, { bookIndex = null, locale = null, previewBook = null, editionType = null } = {}, formatMessage = null) {
	const lang = normalizeCoverLocale(locale || conversation.language);
	const t = resolveCoverFormatMessage(lang, formatMessage);

	return {
		from: getDefaultCoverTitleFrom(conversation, lang, t),
		to: getDefaultCoverTitleTo(conversation, bookIndex, lang, t, { previewBook, editionType }),
	};
}

// Sous-titre personnalisé : stocké par tome sur books[i].cover (comme le titre).
// Lecture volontairement sur le cover brut du tome, sans la fusion de getConversationCover :
// un tome non personnalisé doit garder son sous-titre par défaut (ses propres dates) et non
// hériter du sous-titre global. Le global ne sert plus que de source legacy pour le tome 1
// (conversations d'avant l'isolation par tome, où seul conversation.cover portait le champ).
function getBookRawCover(conversation, bookIndex) {
	if(bookIndex == null || bookIndex < 0) {
		return null;
	}
	return ((conversation.books || [])[bookIndex] || {}).cover || null;
}

export function isCoverToCustomized(conversation, bookIndex = null) {
	const bookCover = getBookRawCover(conversation, bookIndex);
	if(bookCover && bookCover.toCustomized) {
		return true;
	}
	if(bookIndex != null && bookIndex > 0) {
		return false;
	}
	return !!(conversation.cover || {}).toCustomized;
}

export function getCustomCoverTo(conversation, bookIndex = null) {
	if(!isCoverToCustomized(conversation, bookIndex)) {
		return '';
	}
	const bookCover = getBookRawCover(conversation, bookIndex);
	if(bookCover && bookCover.toCustomized) {
		return `${bookCover.to || ''}`.trim();
	}
	return `${(conversation.cover || {}).to || ''}`.trim();
}

export function getEffectiveCoverTitle(conversation, field, bookIndex = null, locale = null, formatMessage = null, options = {}) {
	if(field === 'to') {
		const customTo = getCustomCoverTo(conversation, bookIndex);
		if(customTo) {
			return customTo;
		}
		const lang = normalizeCoverLocale(locale || conversation.language);
		return getDefaultCoverTitleTo(conversation, bookIndex, lang, resolveCoverFormatMessage(lang, formatMessage), options);
	}

	const stored = `${getConversationCoverData(conversation, field, bookIndex) || ''}`.trim();
	if(stored) {
		return stored;
	}
	const defaults = getDefaultCoverTitles(conversation, { bookIndex, locale, ...options }, formatMessage);
	return defaults[field] || '';
}

// Préface : propre à chaque tome (books[i].resume). conversation.resume reste la source
// legacy mono-tome, alignée sur le tome 1 à chaque sauvegarde. On ne l'utilise donc en repli
// que pour le tome 1 : sinon un tome sans préface afficherait celle d'un autre tome.
export function getConversationResume(conversation, bookIndex) {
    if(bookIndex != null && bookIndex >= 0) {
        const bookResume = ((conversation.books || [])[bookIndex] || {}).resume;
        if(bookResume) {
            return bookResume;
        }
        if(bookIndex > 0) {
            return '';
        }
    }
    return conversation.resume || '';
}

export function getConversationParticipants(conversation) {
	return (conversation.participants || []).filter((participant, index, array) => array.findIndex(p => p.name === participant.name) === index);
}

export function getConversationParticipantName(conversation, position = 1, type = 'displayName') {
    position = Math.max(1, position);
    if(conversation.participants && conversation.participants[position - 1] && conversation.participants[position - 1][type]) {
        return conversation.participants[position - 1][type];
    }
    return '';
}

export function getMessageParticipant(conversation, name) {
    return conversation.participants.find(participant => participant.name === name);
}

export function getConversationMessagesCount(conversation, options = {}) {
    const blocks = conversation.messagesBlocks || [];
    if(options.forEdition) {
        const dates = getConversationDates(conversation);
        if(dates.start && dates.end) {
            return blocks
                .filter(block => isMessageBlockIncluded(block, dates.start, dates.end))
                .reduce((total, block) => total + block.count, 0);
        }
    }
    return blocks.reduce((total, block) => total + block.count, 0);
}

export function getConversationOption(conversation, option) {
    return (conversation && conversation.options && conversation.options[option]) || '';
}

export function isConversationBestOf(conversation) {
	if(!conversation) {
		return false;
	}
	return getConversationOption(conversation, 'type') === 'bestof'
		|| (conversation.books || []).some(book => book.type === 'bestof');
}

export function orderItemsIncludeBestOf(store, items) {
	return (items || []).some(item => (
		item.conversationId && isConversationBestOf(getConversation(store, item.conversationId))
	));
}

export function getMessageAttachments(messageAttachmentsText) {
    return (messageAttachmentsText || '').toString().split(MESSAGE_ATTACHMENTS_SEPARATOR)
        .map(attachment => attachment.replace(/\r/g, '').trim())
        .filter(attachment => attachment);
}

/**
 * Fusionne un message CSV (contenu + médias S3) avec l'overlay Mongo (isHidden, éditions).
 * Les upserts Best Of via Message.bulkWrite Mongoose créent attachments: [] — ne pas écraser le CSV.
 */
export function mergeCsvMessageWithEdited(csvMessage, editedMessage) {
	if(!editedMessage) {
		return csvMessage;
	}
	if(editedMessage.reference?.startsWith('x-') && !csvMessage?.reference) {
		return editedMessage;
	}
	const merged = {
		...csvMessage,
		...editedMessage,
	};
	const csvAttachments = (csvMessage?.attachments || []).filter(Boolean);
	const mongoAttachments = (editedMessage.attachments || []).filter(Boolean);
	if(csvAttachments.length && !mongoAttachments.length) {
		merged.attachments = csvAttachments;
	}
	return merged;
}

export function getConversationStatuses() {
    return [CONVERSATION_STATUS_PENDING, CONVERSATION_STATUS_CONFIGURED, CONVERSATION_STATUS_ONVALIDATION, CONVERSATION_STATUS_ONGENERATION, CONVERSATION_STATUS_ONPRODUCTION, CONVERSATION_STATUS_DONE];
}

/**
 * Tomes à imprimer : 'book' et 'bestof' sont des livres papier, 'pdf' est numérique.
 * L'édition choisie à la commande (options.type) prime : une conversation commandée
 * en numérique ne part pas en impression, même si elle a gardé des tomes 'book'
 * enregistrés lors d'un aperçu papier précédent.
 */
export function hasPaperBooks(conversation) {
	if(!conversation || conversation.options?.type === 'pdf') {
		return false;
	}
	return (conversation.books || []).some(book => ['book', 'bestof'].includes(book?.type));
}

/** Nombre de tomes papier d'une conversation (les tomes PDF ne s'impriment pas). */
export function getPaperBooksCount(conversation) {
	if(!hasPaperBooks(conversation)) {
		return 0;
	}
	return (conversation.books || []).filter(book => book?.type !== 'pdf').length;
}

/**
 * Type d'un tome pendant l'édition de l'aperçu. Best Of ne fabrique que le tome 1 : les
 * tomes suivants ne sont pas facturés, mais restent des tomes papier en base pour que leur
 * couverture survive à un aller-retour entre Best Of et collection complète.
 */
export function getEditionBookType(index, editionType) {
	if(editionType === 'bestof') {
		return index === 0 ? 'bestof' : 'book';
	}
	return editionType;
}

/**
 * Tome à persister pendant l'édition : fusionne le tome déjà en base (couverture, préface)
 * avec la pagination courante. Contrairement au payload de commande, aucun tome n'est retiré
 * du tableau : côté serveur Object.assign remplace conversation.books en entier, donc une
 * entrée en moins efface définitivement la couverture du tome correspondant.
 */
export function buildEditionBook(existingBook, index, editionType, pagination = {}) {
	return {
		index,
		...(existingBook || {}),
		...pagination,
		type: getEditionBookType(index, editionType),
	};
}

/**
 * Tome déjà enregistré pour cet index de collection.
 * On cherche d'abord book.index : après une commande partielle, le tableau ne contient
 * plus que les tomes facturés, et books[3] n'est plus le tome 4.
 * Repli sur la position seulement si l'index stocké correspond, ou s'il est absent.
 */
export function findSavedConversationBook(savedBooks, index) {
	const books = savedBooks || [];
	const match = books.find(book => book && Number(book.index) === index);
	if(match) {
		return match;
	}
	const atPosition = books[index];
	if(atPosition && (atPosition.index == null || Number(atPosition.index) === index)) {
		return atPosition;
	}
	return null;
}

/**
 * Fenêtre de tomes à imprimer, numéros inclusifs à partir de 1.
 * null = collection entière. start et end absents : du premier au dernier.
 */
export function resolveCollectionVolumeRange(total, start, end) {
	const count = parseInt(total, 10);
	if(!count || count < 1) {
		return null;
	}
	let from = parseInt(start, 10);
	let to = parseInt(end, 10);
	if(!from || from < 1) {
		from = 1;
	}
	if(!to || to < 1 || to > count) {
		to = count;
	}
	from = Math.min(Math.max(from, 1), count);
	to = Math.min(Math.max(to, 1), count);
	if(from > to) {
		const swap = from;
		from = to;
		to = swap;
	}
	if(from === 1 && to === count) {
		return null;
	}
	return { start: from, end: to };
}

/**
 * Borne à déplacer quand on touche le curseur.
 * null si les deux bornes sont sur le même tome : le sens du geste tranche.
 * La plage reste toujours d'un seul tenant.
 */
export function pickCollectionRangeEdge(start, end, value) {
	const from = parseInt(start, 10);
	const to = parseInt(end, 10);
	const at = parseInt(value, 10);
	if(!from || !to || !at) {
		return null;
	}
	if(at < from || (at === from && at !== to)) {
		return 'start';
	}
	if(at > to || (at === to && at !== from)) {
		return 'end';
	}
	if(from === to) {
		return null;
	}
	return Math.abs(at - from) <= Math.abs(at - to) ? 'start' : 'end';
}

/**
 * Plage continue déjà au panier ou sur une commande (premiers tomes, derniers, ou un milieu).
 * null = rien à restaurer : collection entière, trou dans les index, ou autre édition.
 */
export function inferOrderedVolumeRange(savedBooks, generatedCount, { inCart = false, hasOrder = false } = {}) {
	if(!inCart && !hasOrder) {
		return null;
	}
	const total = parseInt(generatedCount, 10);
	const saved = savedBooks || [];
	if(!total || !saved.length || saved.length >= total) {
		return null;
	}
	if(!saved.every(book => book && (!book.type || book.type === 'book'))) {
		return null;
	}
	const indices = saved.map(book => Number(book.index)).sort((left, right) => left - right);
	if(indices.some(index => !Number.isInteger(index) || index < 0)) {
		return null;
	}
	const contiguous = indices.every((index, position) => index === indices[0] + position);
	if(!contiguous) {
		return null;
	}
	return resolveCollectionVolumeRange(total, indices[0] + 1, indices[indices.length - 1] + 1);
}

export function getConversationColorStatus(status) {
	switch(status) {
		case CONVERSATION_STATUS_CONFIGURED:
			return 'secondary';

		case CONVERSATION_STATUS_ONVALIDATION:
			return 'danger';

		case CONVERSATION_STATUS_ONGENERATION:
			return 'warning';

		case CONVERSATION_STATUS_ONPRODUCTION:
			return 'info';

		case CONVERSATION_STATUS_DONE:
			return 'success';

		case CONVERSATION_STATUS_PENDING:
			return 'success-light';

		default:
			return 'light';
	}
}

export function getAvailableTypes() {
    return Object.keys(CONVERSATIONS_EXTRACTROR_TYPES);
    // ['ios', 'android', 'messenger', 'instagram', 'whatsapp'];
}

export function getAvailableCoverColors(isLegacy = false) {
    return Object.keys(CONVERSATION_COVER_COLORS).filter(key => isLegacy || !CONVERSATION_COVER_COLORS[key].legacy).sort((keyA, keyB) => CONVERSATION_COVER_COLORS[keyA].order - CONVERSATION_COVER_COLORS[keyB].order);
}

export function getCoverColorFromName(name) {
    return CONVERSATION_COVER_COLORS[name];
}

export function getAvailableParticipantColors(isLegacy = false) {
    return Object.keys(CONVERSATION_PARTICIPANT_COLORS)
        .filter(key => isLegacy || !CONVERSATION_PARTICIPANT_COLORS[key].legacy)
        .sort((keyA, keyB) => (CONVERSATION_PARTICIPANT_COLORS[keyA].order || 0) - (CONVERSATION_PARTICIPANT_COLORS[keyB].order || 0));
}

export function getParticipantColorFromName(name) {
    return CONVERSATION_PARTICIPANT_COLORS[name];
}

export function getDefaultParticipantPosition(index) {
    return index % 2 === 0 ? 'right' : 'left';
}

// Ancien ordre d'usine. Une couleur qui est encore celle-ci, à cet index, n'a pas été choisie.
const FACTORY_PARTICIPANT_COLORS = ['blush-clair', 'ciel-clair', 'lavande-claire', 'prune-claire', 'peche-claire', 'sauge-clair', 'miel', 'perle'];

// Première bulle dans la famille de la toile, seconde dans une teinte voisine.
const COVER_BUBBLE_LEAD = {
    sauge: ['sauge-clair', 'ciel-clair'],
    'ciel-doux': ['ciel-clair', 'lavande-claire'],
    peche: ['peche-claire', 'blush-clair'],
    'ivoire-atelier': ['perle', 'peche-claire'],
    lavande: ['lavande-claire', 'ciel-clair'],
    blush: ['blush-clair', 'peche-claire'],
};

export function getParticipantColorsForCover(coverColorName) {
    const all = getAvailableParticipantColors();
    const lead = COVER_BUBBLE_LEAD[coverColorName] || COVER_BUBBLE_LEAD[DEFAULT_COVER_COLOR];
    return [...lead.filter(name => all.includes(name)), ...all.filter(name => !lead.includes(name))];
}

function isAutomaticParticipantColor(participant, index) {
    if(participant.colorCustomized) {
        return false;
    }
    if(participant.colorAuto) {
        return true;
    }
    const palette = getAvailableParticipantColors();
    if(!participant.color || !palette.includes(participant.color)) {
        return true;
    }
    return participant.color === FACTORY_PARTICIPANT_COLORS[index % FACTORY_PARTICIPANT_COLORS.length];
}

export function applyDefaultParticipantColors(participants = [], coverColorName = DEFAULT_COVER_COLOR) {
    const colors = getParticipantColorsForCover(coverColorName);
    return participants.map((participant, index) => {
        const automatic = isAutomaticParticipantColor(participant, index);
        return {
            ...participant,
            color: automatic ? colors[index % colors.length] : participant.color,
            ...(automatic ? { colorAuto: true } : {}),
            position: ['left', 'right'].includes(participant.position)
                ? participant.position
                : getDefaultParticipantPosition(index),
        };
    });
}

// Pose sur chaque tome la couleur d'ouverture effective (palette par index) sans
// écraser un choix utilisateur. Même filet que applyDefaultParticipantColors.
export function applyDefaultCoverColors(conversation = {}) {
    const books = conversation.books || [];
    if(books.length <= 1) {
        return conversation;
    }
    let changed = false;
    const nextBooks = books.map((book, index) => {
        const nextColor = getEffectiveCoverColor(conversation, index);
        const ownColor = (book.cover || {}).color;
        if(ownColor === nextColor) {
            return book;
        }
        changed = true;
        return {
            ...book,
            cover: {
                ...(book.cover || {}),
                color: nextColor,
            },
        };
    });
    if(!changed) {
        return conversation;
    }
    return {
        ...conversation,
        books: nextBooks,
    };
}

export function participantsNeedDefaultColors(participants = [], coverColorName = DEFAULT_COVER_COLOR) {
    const colors = getAvailableParticipantColors();
    if(participants.some(participant => !participant.color || !colors.includes(participant.color)
        || !['left', 'right'].includes(participant.position))) {
        return true;
    }
    const next = applyDefaultParticipantColors(participants, coverColorName);
    return next.some((participant, index) => participant.color !== participants[index].color || !!participant.colorAuto !== !!participants[index].colorAuto);
}

export function isMessageBlockIncluded(messagesBlock, start, end) {
    // console.log('isMessageBlockIncluded');
    // console.log(`
    //     Start
    //     base: ${start}
    //     timezoned: ${moment(start).format()}
    //     utc: ${moment.utc(start).format()}
    //     timezone -> utc: ${moment.utc(moment(start).format())}
    //
    //     base: ${messagesBlock.start}
    //     timezoned: ${moment(messagesBlock.start).format()}
    //     utc: ${moment.utc(messagesBlock.start).format()}
    // `);
    // console.log(`
    //     End
    //     ${end}
    //     ${messagesBlock.end}
    //     ${moment(messagesBlock.end).format()}
    // `);
    const result = (moment(messagesBlock.start).isSameOrBefore(start) && moment(messagesBlock.end).isSameOrAfter(start))
    || (moment(messagesBlock.start).isSameOrBefore(end) && moment(messagesBlock.end).isSameOrAfter(end))
    || (moment(start).isSameOrBefore(messagesBlock.start) && moment(end).isSameOrAfter(messagesBlock.end));
    // console.log(result ? 'IN' : 'OUT');
    return result;
}

// Retrieve conversation files path
// Check multiple sources for legacy purpose
export function getConversationOrderFilesPath(conversation, types = null) {
    return (conversation.files || conversation?.order?.files || [conversation.order?.filePath || `conversations/${conversation._id}/commande_${conversation.order?._id || conversation.order || ''}.zip`])
		.filter(file => {
			if(!types) {
				return true;
			}
			// Typed filters must not match the string zip fallback (no .type → previously passed).
			if(!file || typeof file !== 'object') {
				return false;
			}
			// Legacy entries without type stay included; typed entries must match.
			return !file.type || (Array.isArray(types) ? types : [types]).includes(file.type);
		});
}

export function canDowloandFiles(conversation) {
    return (
		process.env.NODE_ENV === 'development'
		|| [CONVERSATION_STATUS_ONVALIDATION, CONVERSATION_STATUS_ONPRODUCTION, CONVERSATION_STATUS_DONE].includes(conversation.status)
	) && getConversationOrderFilesPath(conversation, 'book').length > 0
	&& conversation.dates?.generated
	&& moment.utc().subtract(7, 'days').isBefore(conversation.dates.generated);
}

export function canEditConversation(conversation, conversationOrders = []) {
	if(!conversation) {
		return false;
	}
	const currentOrder = conversationOrders.find(order => ['pending', 'onpreparation'].includes(order?.status));
	return [CONVERSATION_STATUS_PENDING, CONVERSATION_STATUS_DONE].includes(conversation.status)
		|| (conversation.status === CONVERSATION_STATUS_ONVALIDATION && currentOrder?.status === 'pending');
}

export function getHighlights(conversation, messages = []) {
	console.log('Conversation::: Highlights', conversation, messages.length);
	const startDate = (conversation.dates?.start || messages[0]?.date) && moment(conversation.dates?.start || messages[0]?.date).format('DD-MM-YYYY HH:mm:ss');
	const endDate = (conversation.dates?.end || messages[messages.length - 1]?.date) && moment(conversation.dates?.end || messages[messages.length - 1]?.date).format('DD-MM-YYYY HH:mm:ss');
	const duration = messages[messages.length - 1]?.date && messages[0]?.date && moment(messages[messages.length - 1]?.date).diff(messages[0]?.date, 'days'); // Get duration days from conversation start to now
	let charsCount = 0; // Get chars count
	const messagesCountByDay = {}; // Get day with max messages count

	messages.forEach(message => {
		const messageData = message.messageCsv ? transformCsvToJson(message.messageCsv)[0] : message;
		charsCount += (messageData.text || '').length || 0; // Text
		charsCount += ((messageData.attachments || []).length || 0) * 500; // Attachments
		const dayKey = moment(messageData.date).format('DD-MM-YYYY');
		if(dayKey) {
			messagesCountByDay[dayKey] = messagesCountByDay[dayKey] ? messagesCountByDay[dayKey] + 1 : 1;
		}
	});

	const messagesCountByDayArray = Object.keys(messagesCountByDay).map(day => ({ day, count: messagesCountByDay[day] })).sort((statA, statB) => statA.count - statB.count);
	const specialDay = messagesCountByDayArray?.length ? messagesCountByDayArray.pop() : null;
	const highlights = {
		startDate,
		endDate,
		duration,
		messagesCount: messages.length,
		charsCount,
		// typingHours: Math.round(charsCount / 3000),
		// charsCountReferenceDiff: Math.round(charsCount / 93000),
		specialDay: specialDay?.day || null, // Special day date
		specialDayMessagesCount: specialDay?.count || null, // Get messages count on this day
		imagesCount: conversation.imagesCount || 0,
		mediasCount: conversation.mediasCount || 0,
	};
	console.log('Conversation::: Highlights', highlights);
	return highlights;
}

// Get collector information by provider from CONVERSATIONS_EXTRACTROR_TYPES data
export function getActiveCollector(provider, nameOrCode = '') {
    // console.log('getActiveCollector', provider, nameOrCode);
    if(!provider || !CONVERSATIONS_EXTRACTROR_TYPES[provider]) {
        return null;
    }
    const { collectors = [], ...extractorData } = CONVERSATIONS_EXTRACTROR_TYPES[provider];
    const defaultCollector = (collectors?.sort((cA, cB) => cA.sort - cB.sort) || [])[0] || {};
    let activeCollector = {
        collectors,
        ...extractorData,
        ...defaultCollector,
    };
    if(nameOrCode && collectors?.find(collector => [collector.name, collector.code].includes(nameOrCode))) {
        activeCollector = {
            collectors,
            ...extractorData,
            ...collectors.find(collector => [collector.name, collector.code].includes(nameOrCode)),
        };
    }
    // console.log('active collector', activeCollector);
    return activeCollector;
}

// Export Actions
export function setConversations(conversations, isSet = false) {
    return {
        type: SET_CONVERSATIONS,
        conversations,
        isSet,
    };
}
export function removeConversation(conversationId) {
    return {
        type: REMOVE_CONVERSATION,
        conversationId,
    };
}

export function setConversationMessages(conversationId, messages) {
    return {
        type: SET_CONVERSATION_MESSAGES,
        conversationId,
        messages,
    };
}

export function updateConversationMessage(conversationId, message) {
	return {
		type: UPDATE_CONVERSATION_MESSAGE,
		conversationId,
		message,
	};
}

export function updateConversationMessagesVisibility(conversationId, references, isHidden) {
	return {
		type: UPDATE_CONVERSATION_MESSAGES_VISIBILITY,
		conversationId,
		references,
		isHidden,
	};
}

// Remet chaque message dans l'état capturé avant une mise à jour optimiste
// (isHidden et isEdited d'origine), quand l'appel API a échoué.
export function restoreConversationMessagesVisibility(conversationId, snapshot) {
	return {
		type: UPDATE_CONVERSATION_MESSAGES_VISIBILITY,
		conversationId,
		references: snapshot.map(entry => entry.reference),
		snapshot,
	};
}

export function setBookGeneration(conversationId, start, end, books, format = 'a5') {
    return {
        type: SET_BOOK_GENERATION,
        conversationId,
        start,
        end,
        books,
        format,
    };
}

export function setTutorial(key, value = TUTORIAL_VERSION) {
    return {
        type: SET_TUTORIAL,
        key,
        value,
    };
}
